<template>
    <div class="dropdown dropleft language-switcher">
        <!-- Dropdown button -->
        <button
            class="btn dropdown-toggle p-0 language-switcher-toggle"
            type="button"
            id="languageDropdown"
            @click="toggleDropdown"
            aria-label="Language selector"
            title=""
        >
            <img
                class="flag-icon"
                :src="getFlagSrc(currentLanguage)"
                :alt="currentLanguage"
            />
        </button>

        <!-- Dropdown menu -->
        <div
            v-if="selectableLanguages.length"
            v-show="showDropdown"
            class="dropdown-menu show language-dropdown"
        >
            <div
                v-for="language in selectableLanguages" :key="language">
                <button
                    class="dropdown-item d-flex align-items-center"
                    @click="switchLanguage(language)"
                >
                    <img
                        class="flag-icon mr-2"
                        :src="getFlagSrc(language)"
                        :alt="language"
                    />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, onBeforeUnmount, watch, getCurrentInstance, computed } from 'vue';
    import i18n from '../utils/locale';
    import { PLAYER_COMMUNICATION } from '../enums/player-communication';

    const app = getCurrentInstance();

    const showDropdown = ref(false);

    // Get the list of available languages
    const availableLanguages = Object.keys(i18n.messages);

    const selectableLanguages = computed(() => availableLanguages.filter(
        (lang) => lang !== currentLanguage.value,
    ));

    // Reactive reference for the current language
    const currentLanguage = ref(i18n.locale);

    // Watch for changes in i18n.locale and update currentLanguage
    watch(
        () => i18n.locale,
        (newLocale) => {
            currentLanguage.value = newLocale;
        },
        { immediate: true },
    );

    const toggleDropdown = () => {
        showDropdown.value = !showDropdown.value;
    };

    const switchLanguage = (language) => {
        app.proxy.$store.dispatch('setLocale', language);
        showDropdown.value = false; // Close the dropdown
    };

    const getFlagSrc = (languageCode) => {
        if (window.playerCommunication === PLAYER_COMMUNICATION.IPC) {
            return `../../app/assets/countries/${languageCode}.svg`;
        }

        return `/images/apps/assets/countries/${languageCode}.svg`;
    };

    // Close the dropdown when clicking outside
    const closeDropdown = () => {
        showDropdown.value = false;
    };

    const handleClickOutside = (event) => {
        const dropdown = document.querySelector('.language-switcher');
        const dropdownToggle = document.querySelector('.language-switcher-toggle');

        if (!dropdown || !dropdownToggle) {
            closeDropdown();
            return;
        }

        if (!dropdown.contains(event.target) && !dropdownToggle.contains(event.target)) {
            closeDropdown();
        }
    };

    // Set up event listener for clicking outside the dropdown
    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside);
    });
</script>

<style scoped lang="scss">
    .language-switcher .flag-icon {
        width: 24px;
        height: 24px;
        border-radius: 4px;
        object-fit: cover;
    }

    .language-switcher .dropdown-toggle::before,
    .language-switcher .dropdown-toggle::after {
        content: none !important; /* Remove the default content */
    }

    .dropdown-menu {
        display: none;
        min-width: 40px;
        max-width: 50px;
        padding: 0;
        position: absolute;
        z-index: 1000;
        background-color: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin-top: 0;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        margin-left: 15px !important;
    }

    .dropdown-menu.show {
        display: block; /* Ensures the dropdown is visible when "show" is added */
    }

    .dropdown-item {
        padding: 8px 12px;
        width: auto;
        min-width: auto;
        cursor: pointer;
    }

    .dropdown-item:hover {
        background-color: #f8f9fa;
        min-width: 40px;
        max-width: 48px;
    }
</style>
